import { ReactElement, useEffect, useRef, useState } from "react";
import Dirs from "../../fmanlibs/Dirs";
import { FMFile } from "../../shared/FMFile";
import FManApi from "../../fmanlibs/FManApi";
import TextContent from "./TextContent";
import ImageContent from "./ImageContent";
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider";
import { useOverlay } from "../../gui/Overlay/OverlayProvider";
import CloseIcon from "../../gui/Icons/CloseIcon";
import IconButton from "../../gui/Button/IconButton";
import { useFilesProvider } from "../../fmanlibs/FilesProvider";
import { usePath } from "../../libs/path/PathProvider";
import VideoContent from "./VideoContent";
import AudioContent from "./AudioContent";
import SaveIcon from "../../gui/Icons/SaveIcon";
import Spinner from "../../gui/Loading/Spinner";
import Files from "../../fmanlibs/Files";

const DIALOG_ID="preview"

export function PreviewDlg(props:{file:FMFile}) {
    const {file} = props
    const [content,setContent] = useState<string|null>(null)
    const [url,setUrl] = useState<string|null>(null)
    const parent = Dirs.i.findNode(file.parentId!)
    const filename = (parent?.path||"")+"/"+file.name
    const {addSnackbar} = useSnackbar()
    const {popOverlay} = useOverlay()
    const [dirty,setDirty] = useState<boolean>(false)
    const [busy,setBusy] = useState<boolean>(false)
    const original = useRef<string>("")

    const onChange = (text:string|undefined) => {
        setContent(text||"")
        setDirty(original.current!=text)
    }

    let preview :ReactElement|null = null
    if (content!==null) preview = <TextContent text={content} onChange={onChange}/>
    else if (url!==null) {
        if (FMFile.isVideo(file)) preview = <VideoContent url={url}/>
        else if (FMFile.isAudio(file)) preview = <AudioContent url={url}/>
        else preview = <ImageContent url={url}/>
    }
    else preview = <Spinner/>

    const onError = (e:any) => {
        addSnackbar(e)
        popOverlay(DIALOG_ID)
    }

    const onSave = async () => {
        if (busy || content===null) return
        else setBusy(true)
        try {
            const buffer = new TextEncoder().encode(content)
            const size = buffer.length
            if (size>FMFile.MAX_FILE_SIZE) throw "File exceeds maxmum file size"

            const partial = await Files.i.replaceFile(file._id, size)
            partial.parentId && Files.i.expire(partial.parentId).get(partial.parentId)

            let offset = 0
            while(offset<size){
                const end = Math.min(offset+FMFile.CHUNK, size)
                await Files.i.appendFile(partial._id, offset, buffer.slice(offset, end))
                offset += FMFile.CHUNK
            }
            original.current = content
            setDirty(false)
            addSnackbar("", "Saved", "info", 1000)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    const save = FMFile.isText(file) ? <IconButton onClick={onSave} disabled={busy || !dirty}><SaveIcon/></IconButton> : null
    useEffect(() => {
        if (FMFile.isText(file)) {
            FManApi.i.getContent(file._id).then(content => {
                setContent(content)
                original.current = content
            }).catch(onError)
        }
        else FManApi.i.shareFiles([file._id], 1).then(url => setUrl(url)).catch(onError)
    }, [])

    return <>
        <div className="header flex middle between"><div className="name">{filename}</div>
            <div className="flex gap-8">
                {save}
                <IconButton onClick={_ => popOverlay(DIALOG_ID)}><CloseIcon/></IconButton>
            </div>
        </div>
        <div className="content">{preview}</div>
    </>
}
export default function usePreviewDlg(){
    const {addOverlay,hasOverlay} = useOverlay()
    const {files} = useFilesProvider()
    const {setHash} = usePath()

    const afterClosed = () => setHash("")
    const openPreview = (fileId:string) => {
        const file = files.find(f => f._id==fileId)
        if (!file || hasOverlay(DIALOG_ID)) return

        addOverlay("preview", {children:<PreviewDlg file={file}/>, afterClosed, className:FMFile.isAudio(file) ? "dialog" : "fullscreen"})
    }
    return {openPreview}
}